import { useSyncExternalStore } from 'react';

/**
 * In-app alert pop-up settings (Notification Center → Pop-up Settings): WHERE the pop-up stack sits
 * and HOW each pop-up closes. There can be several NAMED settings (2026-10-14); each alert arrives
 * carrying the one that applies to it (`alert.popup` — its rule's setting, else the Default).
 *
 * The module store below holds only the DEFAULT setting, loaded once after sign-in from
 * `GET /api/v1/app-config` (AppShell): the fallback for an alert that carries none. Same
 * module-store pattern as `utils/sdmsRollbackUi.ts`.
 *
 * A rule marked ACK REQUIRED always needs Acknowledge, whatever the setting.
 */
export const ALERT_POPUP_POSITIONS = [
  'top-left', 'top-center', 'top-right',
  'middle-left', 'center', 'middle-right',
  'bottom-left', 'bottom-center', 'bottom-right',
] as const;
export type AlertPopupPosition = (typeof ALERT_POPUP_POSITIONS)[number];

/** `auto` = closes after N seconds · `ack` = stays until Acknowledge. */
export type AlertPopupMode = 'auto' | 'ack';

export interface AlertPopupSettings {
  position: AlertPopupPosition;
  mode: AlertPopupMode;
  seconds: number;
}

export const ALERT_POPUP_MIN_SECONDS = 3;
export const ALERT_POPUP_MAX_SECONDS = 120;
export const DEFAULT_ALERT_POPUP: AlertPopupSettings = { position: 'bottom-right', mode: 'auto', seconds: 8 };

/** Human label for a position, e.g. "Bottom right". */
export function positionLabel(p: AlertPopupPosition): string {
  if (p === 'center') return 'Center';
  const [row = '', col = ''] = p.split('-');
  return `${row.charAt(0).toUpperCase()}${row.slice(1)} ${col}`;
}

/** One-line description, e.g. "Bottom right · auto-close 8 s". */
export function describeAlertPopup(s: AlertPopupSettings): string {
  return `${positionLabel(s.position)} · ${s.mode === 'ack' ? 'require acknowledge' : `auto-close ${s.seconds} s`}`;
}

/** Coerce whatever the server (or an old client) sent into valid settings. */
export function normalizeAlertPopup(v: Partial<AlertPopupSettings> | null | undefined): AlertPopupSettings {
  const position = (ALERT_POPUP_POSITIONS as readonly string[]).includes(v?.position ?? '')
    ? (v!.position as AlertPopupPosition) : DEFAULT_ALERT_POPUP.position;
  const mode: AlertPopupMode = v?.mode === 'ack' ? 'ack' : 'auto';
  const raw = Number(v?.seconds);
  const seconds = Number.isFinite(raw)
    ? Math.min(ALERT_POPUP_MAX_SECONDS, Math.max(ALERT_POPUP_MIN_SECONDS, Math.round(raw)))
    : DEFAULT_ALERT_POPUP.seconds;
  return { position, mode, seconds };
}

let current: AlertPopupSettings = DEFAULT_ALERT_POPUP;
const listeners = new Set<() => void>();

/** Apply new settings app-wide (from app-config, or right after a save). */
export function setAlertPopupSettings(v: Partial<AlertPopupSettings> | null | undefined): void {
  const next = normalizeAlertPopup(v);
  if (next.position === current.position && next.mode === current.mode && next.seconds === current.seconds) return;
  current = next;
  for (const l of listeners) l();
}

/** The current settings (non-reactive read). */
export function getAlertPopupSettings(): AlertPopupSettings {
  return current;
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** Reactive hook — re-renders when the settings change. */
export function useAlertPopupSettings(): AlertPopupSettings {
  return useSyncExternalStore(subscribe, getAlertPopupSettings, getAlertPopupSettings);
}

/** The pop-up setting that applies to ONE alert: the one the server resolved for its rule (the
 *  rule's assigned setting, else the Default), falling back to `fallback` (the Default loaded at
 *  sign-in) for an alert that carries none — e.g. the Pop-up Settings "Show test pop-up". */
export function effectivePopup(alert: { popup?: Partial<AlertPopupSettings> | null }, fallback: AlertPopupSettings): AlertPopupSettings {
  return alert.popup ? normalizeAlertPopup(alert.popup) : fallback;
}

/** Does this alert need an Acknowledge click? (rule's ACK REQUIRED, or its pop-up setting says
 *  "stay until Acknowledge"). ACK REQUIRED always wins, whatever the setting — except that an
 *  auto-resolved alert (its condition cleared) never needs one. */
export function needsAck(
  alert: { ackRequired: boolean; ackAt?: string | null; resolvedAt?: string | null; popup?: Partial<AlertPopupSettings> | null },
  fallback: AlertPopupSettings,
): boolean {
  return !alert.ackAt && !alert.resolvedAt && (alert.ackRequired || effectivePopup(alert, fallback).mode === 'ack');
}
